import { Component } from '@theme/component';

/**
 * Zager BOGO promo banner — fixed bottom bar with a dismiss control and a
 * countdown tile.
 *
 * Ported from `footer_banner.php` (see docs/final-blocks-build-sheet.md
 * §2.13). WP binds the dismiss handler twice (`footer_banner.php:274` and
 * `footer.php:281`) against the same `localStorage.bannerClosedAt` key —
 * this component implements it once. The `$dailyDeals` weekday copy table
 * is dead code (line 82 always selects `all_day`) and is not ported.
 *
 * The countdown is native `setInterval` — no plugin, replacing the
 * HurryTimer shortcode WP only used for the ticking numbers.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} dismiss - Closes the bar.
 * @property {HTMLElement} countdown - The whole countdown tile, hidden when the target is blank/past.
 * @property {HTMLElement} countdownValue - The `days:hrs:mins:secs` text node's container.
 *
 * @extends {Component<Refs>}
 */
class ZagerPromoBannerComponent extends Component {
  requiredRefs = ['dismiss', 'countdown', 'countdownValue'];

  /** @type {number | undefined} */
  #intervalId;

  connectedCallback() {
    super.connectedCallback();

    if (this.#isDismissed()) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.#tick();
    this.#intervalId = window.setInterval(() => this.#tick(), 1000);
    this.addEventListener('click', this.#navigate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#intervalId);
    this.removeEventListener('click', this.#navigate);
  }

  /**
   * Live makes the whole strip clickable twice over: the grey panel is wrapped
   * in an `<a>` and `.hthb-notification-content` carries an `onclick`
   * (`footer_banner.php:98`), so a click anywhere but the close control opens
   * the sale page. Reproduced as one delegated handler rather than a wrapping
   * anchor, because the dismiss button may not be nested inside an anchor.
   *
   * The CTA anchor and the dismiss button keep their own behaviour, and any
   * click that is not a plain left click (modifier held, "open in new tab",
   * middle click — which fires `auxclick`, not `click`) is left alone.
   *
   * @param {MouseEvent} event - The delegated click.
   */
  #navigate = (event) => {
    const link = this.getAttribute('data-banner-link');

    if (!link) return;
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target instanceof Element ? event.target : null;

    if (!target) return;
    if (target.closest('a, button, [role="button"], input, select, textarea')) return;

    window.location.href = link;
  };

  /**
   * Dismisses the bar for `data-dismiss-minutes` minutes, persisted across
   * page loads via `localStorage`.
   */
  dismiss() {
    window.localStorage.setItem('bannerClosedAt', String(Date.now()));
    this.hidden = true;
    window.clearInterval(this.#intervalId);
  }

  /**
   * @returns {boolean} True while the bar is still inside its dismissal window.
   */
  #isDismissed() {
    const raw = window.localStorage.getItem('bannerClosedAt');

    if (!raw) return false;

    const closedAt = Number(raw);
    const dismissMinutes = Number(this.getAttribute('data-dismiss-minutes')) || 15;

    if (Number.isNaN(closedAt)) return false;

    return Date.now() - closedAt < dismissMinutes * 60000;
  }

  /**
   * Milliseconds until the next midnight in `timeZone`.
   *
   * Live's HurryTimer campaign 160542 is `mode = 3` (recurring),
   * `recurring_frequency = daily`, `recurring_start_time = 12:00 AM`,
   * `_hurryt_recurring_duration = 24h` — a rolling 24-hour clock that resets
   * every midnight in the WordPress site timezone (`America/Chicago`), NOT a
   * campaign with a fixed end date. Confirmed against `wp_postmeta` and by
   * reading the live clock twice: both readings resolved to the same
   * upcoming Central midnight.
   *
   * Reads the wall-clock time in the target zone via `Intl` and subtracts it
   * from 24h, so it follows the zone's DST rules without shipping a table.
   *
   * @param {string} timeZone - IANA zone name.
   * @returns {number} Milliseconds remaining until that zone's next midnight.
   */
  #msUntilMidnightIn(timeZone) {
    const now = new Date();
    let parts;

    try {
      parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).formatToParts(now);
    } catch {
      // An invalid zone in the editor must not stop the clock.
      parts = new Intl.DateTimeFormat('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).formatToParts(now);
    }

    const at = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    // `hour12: false` renders midnight as 24 in some ICU versions.
    const elapsed = (at('hour') % 24) * 3600 + at('minute') * 60 + at('second');

    return (86400 - elapsed) * 1000 - now.getMilliseconds();
  }

  /**
   * Renders the current `days:hrs:mins:secs` remaining, or hides just the
   * countdown tile (keeping the rest of the bar) once a *fixed* target is
   * blank or has passed. A daily countdown never expires — it rolls over.
   */
  #tick() {
    const daily = this.getAttribute('data-countdown-mode') === 'daily';
    let remainingMs;

    if (daily) {
      remainingMs = this.#msUntilMidnightIn(this.getAttribute('data-countdown-timezone') || 'America/Chicago');
    } else {
      const target = this.getAttribute('data-countdown-end');
      const targetMs = target ? Date.parse(target) : NaN;
      remainingMs = Number.isNaN(targetMs) ? -1 : targetMs - Date.now();
    }

    if (remainingMs <= 0) {
      this.refs.countdown.hidden = true;
      window.clearInterval(this.#intervalId);
      return;
    }

    const totalSeconds = Math.floor(remainingMs / 1000);
    const values = [
      Math.floor(totalSeconds / 86400),
      Math.floor((totalSeconds % 86400) / 3600),
      Math.floor((totalSeconds % 3600) / 60),
      totalSeconds % 60,
    ];

    this.#build();

    // Live hides the days block, and the separator that follows it, once days
    // reaches zero — the clock reads "21 HRS : 46 MINS : 01 SECS". Unit labels
    // are formatting tokens matching the live rendering, not editable copy.
    const hideDays = values[0] === 0;

    this.#units.forEach((unit, index) => {
      const digit = unit.querySelector('.zager-promo-banner__digit');
      if (digit) digit.textContent = String(values[index]).padStart(2, '0');
      if (index === 0) unit.hidden = hideDays;
    });

    if (this.#separators[0]) this.#separators[0].hidden = hideDays;
  }

  /** @type {HTMLElement[]} */
  #units = [];

  /** @type {HTMLElement[]} */
  #separators = [];

  /**
   * Builds the digit/label blocks once, so each tick only rewrites the numbers
   * instead of re-parsing markup every second.
   */
  #build() {
    if (this.#units.length) return;

    const container = this.refs.countdownValue;
    container.textContent = '';

    ['DAYS', 'HRS', 'MINS', 'SECS'].forEach((label, index) => {
      if (index > 0) {
        const sep = document.createElement('span');
        sep.className = 'zager-promo-banner__sep';
        sep.setAttribute('aria-hidden', 'true');
        sep.textContent = ':';
        container.append(sep);
        this.#separators.push(sep);
      }

      const unit = document.createElement('span');
      unit.className = 'zager-promo-banner__unit';

      const digit = document.createElement('span');
      digit.className = 'zager-promo-banner__digit';
      digit.textContent = '00';

      const caption = document.createElement('span');
      caption.className = 'zager-promo-banner__label';
      caption.textContent = label;

      unit.append(digit, caption);
      container.append(unit);
      this.#units.push(unit);
    });
  }
}

if (!customElements.get('zager-promo-banner-component')) {
  customElements.define('zager-promo-banner-component', ZagerPromoBannerComponent);
}
